import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, Users2, Handshake, TrendingUp, HandCoins, BadgeCheck, Wallet, ArrowRight, ClipboardSignature, IdCard, Eye, Trophy, Target, Calendar } from "lucide-react";
import TierBadge, { TierChip } from "@/components/TierBadge";
import CandidateDetailModal from "@/components/CandidateDetailModal";

const stages = [
  { key: "recommended", label: "Recomendado" },
  { key: "interview", label: "Entrevista" },
  { key: "selected", label: "Selecionado" },
  { key: "started", label: "Iniciou funções" },
  { key: "validated", label: "Período mínimo" },
  { key: "bonus_approved", label: "Prémio aprovado" },
  { key: "scheduled", label: "Pagamento agendado" },
  { key: "paid", label: "Pago" },
];

function stageIndex(cand, bonuses) {
  if (cand.stage === "rejected" || cand.stage === "withdrawn") return -1;
  const bonus = bonuses.find((b) => b.candidate_id === cand.id);
  if (bonus?.status === "paid") return 7;
  if (bonus?.status === "scheduled") return 6;
  if (bonus?.status === "approved") return 5;
  if (cand.stage === "validated") return 4;
  if (cand.stage === "started") return 3;
  if (cand.stage === "selected") return 2;
  if (cand.stage === "interview") return 1;
  return 0;
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="card-surface p-6" data-testid={testId}>
      <div className="flex items-center gap-3 text-[var(--ink-soft)]">
        <div className={`w-9 h-9 rounded-xl grid place-items-center ${accent}`}><Icon className="w-4 h-4"/></div>
        <div className="eyebrow !text-[var(--ink-soft)]">{label}</div>
      </div>
      <div className="font-heading text-4xl mt-3 text-[var(--ink)]">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [referrer, setReferrer] = useState(null);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: ref } = await api.get("/referrers/me");
        setReferrer(ref);
        if (!ref) { setLoading(false); return; }
        const [s, c, b] = await Promise.all([
          api.get("/stats/referrer"),
          api.get("/candidates?mine=true"),
          api.get("/bonuses?mine=true"),
        ]);
        setStats(s.data); setCandidates(c.data); setBonuses(b.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-[var(--ink-soft)]">A carregar…</div>;

  if (!referrer) {
    return (
      <div className="card-surface p-10" data-testid="dashboard-empty-profile">
        <div className="eyebrow">Onboarding</div>
        <h1 className="font-heading text-4xl mt-2">Complete o seu perfil</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-xl">Para poder recomendar candidatos, precisamos dos seus dados fiscais e de contacto. Demora 2 minutos.</p>
        <Link to="/onboarding/profile" className="pill-btn pill-primary mt-6" data-testid="cta-complete-profile">
          <IdCard className="w-4 h-4"/> Completar perfil
        </Link>
      </div>
    );
  }

  if (!referrer.protocol_signed) {
    return (
      <div className="card-surface p-10" data-testid="dashboard-needs-protocol">
        <div className="eyebrow">Onboarding · Passo 2</div>
        <h1 className="font-heading text-4xl mt-2">Assine o protocolo</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-xl">Antes de visualizar vagas e recomendar candidatos, precisa de assinar digitalmente o protocolo do programa.</p>
        <Link to="/onboarding/protocol" className="pill-btn pill-primary mt-6" data-testid="cta-sign-protocol">
          <ClipboardSignature className="w-4 h-4"/> Assinar protocolo
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Olá, {referrer.full_name.split(" ")[0]}</div>
          <h1 className="font-heading text-5xl mt-2 text-[var(--ink)]">O seu painel</h1>
          <div className="mt-2 text-sm text-[var(--ink-soft)] flex items-center gap-2 flex-wrap">
            Código de referência: <span className="chip chip-neutral" data-testid="referrer-code">{referrer.code}</span>
            {stats?.tier && <TierChip tier={stats.tier} />}
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/vacancies" className="pill-btn pill-primary" data-testid="btn-view-vacancies">
            <Briefcase className="w-4 h-4"/> Ver vagas <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        <StatCard icon={Briefcase} label="Vagas abertas" value={stats.open_vacancies} accent="bg-[var(--muted)] text-[var(--forest)]" testId="stat-open-vacancies" />
        <StatCard icon={Users2} label="Recomendados" value={stats.recommended} accent="bg-[rgba(59,113,151,0.12)] text-[var(--info)]" testId="stat-recommended" />
        <StatCard icon={Handshake} label="Entrevistas" value={stats.interviews} accent="bg-[rgba(217,108,74,0.12)] text-[var(--terracotta)]" testId="stat-interviews" />
        <StatCard icon={TrendingUp} label="Contratações" value={stats.hires} accent="bg-[rgba(45,90,39,0.12)] text-[var(--success)]" testId="stat-hires" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={HandCoins} label="Prémios pendentes" value={stats.pending_bonuses} accent="bg-[var(--muted)] text-[var(--terracotta)]" testId="stat-pending" />
        <StatCard icon={BadgeCheck} label="Prémios pagos" value={stats.paid_bonuses} accent="bg-[rgba(45,90,39,0.12)] text-[var(--success)]" testId="stat-paid" />
        <div className="card-surface p-6 bg-gradient-to-br from-[var(--forest)] to-[var(--forest-deep)] text-white border-transparent" data-testid="stat-total-earned">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center"><Wallet className="w-4 h-4"/></div>
            <div className="eyebrow !text-white/70">Total recebido</div>
          </div>
          <div className="font-heading text-4xl mt-3">{Number(stats.total_earned).toFixed(0)} €</div>
          <div className="text-xs text-white/60 mt-1">Pagamentos em Cartão Dá</div>
        </div>
      </div>

      {stats.tier && (
        <section>
          <TierBadge tier={stats.tier} />
          {stats.bonus_cycle_count > 0 && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--muted)] border border-[var(--border)] text-xs" data-testid="cycle-chip">
              <span>🔁</span>
              <span className="font-medium text-[var(--ink)]">{stats.bonus_cycle_count + 1}º ciclo de prémios</span>
              <span className="text-[var(--ink-soft)]">· {stats.bonus_cycle_count} ciclo{stats.bonus_cycle_count > 1 ? "s" : ""} completo{stats.bonus_cycle_count > 1 ? "s" : ""}</span>
            </div>
          )}
        </section>
      )}

      {/* Rank + Next badge */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-6" data-testid="rank-card">
          <div className="flex items-center gap-3 text-[var(--ink-soft)]">
            <div className="w-9 h-9 rounded-xl bg-[rgba(217,108,74,0.12)] text-[var(--terracotta)] grid place-items-center"><Trophy className="w-4 h-4"/></div>
            <div className="eyebrow !text-[var(--ink-soft)]">Ranking global</div>
          </div>
          {stats.rank ? (
            <>
              <div className="font-heading text-4xl mt-3 text-[var(--ink)]">#{stats.rank} <span className="text-lg text-[var(--ink-soft)]">de {stats.total_referrers}</span></div>
              <div className="text-xs text-[var(--ink-soft)] mt-1">Por número de contratações validadas</div>
            </>
          ) : (
            <div className="mt-3 text-sm text-[var(--ink-soft)]">Assim que tiver a primeira contratação validada, a sua posição aparece aqui.</div>
          )}
        </div>

        <div className="card-surface p-6" data-testid="next-badge-card">
          <div className="flex items-center gap-3 text-[var(--ink-soft)]">
            <div className="w-9 h-9 rounded-xl bg-[rgba(45,90,39,0.12)] text-[var(--forest)] grid place-items-center"><Target className="w-4 h-4"/></div>
            <div className="eyebrow !text-[var(--ink-soft)]">Próximo prémio de emblema</div>
          </div>
          {stats.next_badge ? (
            <>
              <div className="font-heading text-2xl mt-3 text-[var(--ink)]">
                {stats.next_badge.label} · <span className="text-[var(--success)]">+{Number(stats.next_badge.amount).toFixed(0)} €</span>
              </div>
              <div className="text-sm text-[var(--ink-soft)] mt-1">
                Faltam <strong className="text-[var(--terracotta)]">{stats.next_badge.missing}</strong> contratações para atingir <strong>{stats.next_badge.threshold}</strong>{stats.bonus_cycle_count > 0 && <> · <span className="chip chip-neutral !py-0.5">ciclo #{stats.next_badge.cycle_number}</span></>}.
              </div>
              <div className="mt-3 h-2 rounded-full bg-[var(--muted)] overflow-hidden">
                <div className="h-full bg-[var(--forest)]" style={{ width: `${Math.min(100, ((stats.bonus_cycle_progress || 0) / stats.next_badge.threshold) * 100)}%` }}/>
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm text-[var(--ink-soft)]">Já atingiu todos os patamares de emblema deste ciclo. Continue a recomendar — o contador reinicia automaticamente para um novo ciclo de prémios.</div>
          )}
        </div>
      </section>

      {/* Funnel */}
      <section>
        <h2 className="font-heading text-3xl mb-4">O seu funil</h2>
        <div className="card-surface p-6" data-testid="funnel-block">
          {(() => {
            const f = stats.funnel || {};
            const rows = [
              { k: "recommended", label: "Recomendados", val: f.recommended || 0, color: "var(--info)" },
              { k: "interview",   label: "Entrevistas",  val: f.interview   || 0, color: "var(--terracotta)" },
              { k: "selected",    label: "Selecionados", val: f.selected    || 0, color: "var(--terracotta)" },
              { k: "started",     label: "Iniciaram funções", val: f.started || 0, color: "var(--forest)" },
              { k: "validated",   label: "Contratações validadas", val: f.validated || 0, color: "var(--success)" },
            ];
            const max = Math.max(1, ...rows.map(r => r.val));
            return (
              <div className="space-y-3">
                {rows.map((r) => (
                  <div key={r.k} data-testid={`funnel-${r.k}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--ink)]">{r.label}</span>
                      <strong>{r.val}</strong>
                    </div>
                    <div className="h-3 rounded-full bg-[var(--muted)] overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${(r.val / max) * 100}%`, background: r.color }}/>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Monthly chart */}
      {Array.isArray(stats.monthly) && stats.monthly.length > 0 && (
        <section>
          <h2 className="font-heading text-3xl mb-4 flex items-center gap-2"><Calendar className="w-6 h-6 text-[var(--forest)]"/> Recomendações por mês</h2>
          <div className="card-surface p-6" data-testid="monthly-chart">
            {(() => {
              const max = Math.max(1, ...stats.monthly.map(m => m.count));
              return (
                <div className="flex items-end gap-3 h-40">
                  {stats.monthly.map((m) => {
                    const pct = (m.count / max) * 100;
                    const [y, mm] = m.month.split("-");
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`month-${m.month}`}>
                        <div className="text-xs text-[var(--ink-soft)]">{m.count}</div>
                        <div className="w-full bg-[var(--forest)]/80 rounded-t transition-all" style={{ height: `${pct}%`, minHeight: m.count ? "4px" : "0" }}/>
                        <div className="text-[10px] text-[var(--ink-soft)] uppercase font-mono">{mm}/{y.slice(-2)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-heading text-3xl mb-4">Progresso das suas recomendações</h2>
        {candidates.length === 0 ? (
          <div className="card-surface p-8 text-center">
            <p className="text-[var(--ink-soft)]">Ainda não recomendou candidatos. Comece pelas vagas abertas.</p>
            <Link to="/vacancies" className="pill-btn pill-primary mt-4 inline-flex" data-testid="empty-vacancies-cta">Ver vagas <ArrowRight className="w-4 h-4"/></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {candidates.slice(0, 8).map((c) => {
              const idx = stageIndex(c, bonuses);
              const rejected = c.stage === "rejected" || c.stage === "withdrawn";
              return (
                <div key={c.id} className="card-surface p-5" data-testid={`candidate-row-${c.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-[var(--ink)]">{c.name}</div>
                      <div className="text-xs text-[var(--ink-soft)]">{c.locality} · {c.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`chip ${rejected ? "chip-neutral" : idx >= 5 ? "chip-green" : "chip-blue"}`}>
                        {rejected ? (c.stage === "rejected" ? "Rejeitado" : "Desistiu") : stages[Math.max(idx, 0)].label}
                      </div>
                      <button
                        onClick={() => setDetailId(c.id)}
                        className="pill-btn pill-ghost !py-1.5 !px-3"
                        data-testid={`view-my-candidate-${c.id}`}
                      >
                        <Eye className="w-3.5 h-3.5"/> Ver ficha
                      </button>
                    </div>
                  </div>
                  {!rejected && (
                    <div className="mt-4 flex items-center gap-1">
                      {stages.map((s, i) => (
                        <div key={s.key} className="flex items-center flex-1">
                          <div className={`stage-dot ${i <= idx ? (i === idx ? "active" : "done") : ""}`}></div>
                          {i < stages.length - 1 && <div className={`stage-line ${i < idx ? "done" : ""}`}></div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {detailId && <CandidateDetailModal candidateId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
